<template>
  <div class="order-files">
    <fieldset>
      <legend class="d-flex">
        {{ $t('order.files.title') }}
      </legend>

      <fieldset
        v-if="!readOnly && (orderID || isCustomerRequest)"
        class="mb-3">
        <legend>
          {{ $t('order.files.addingFile') }}
        </legend>

        <label class="w-100 d-flex align-items-center">
          <file-input
            v-if="uploadInputVisible"
            :disabled="uploadInProgress"
            :onChange="setFiles"
            id="orderFile"
            :multiple="true" />
          <div
            v-if="uploadInProgress"
            class="spinner-border spinner-border-sm ml-4"
            role="status"></div>
        </label>
      </fieldset>

      <d-alert
        v-if="!readOnly && !orderID"
        show
        theme="warning"
        class="text-center mb-0">
        {{ $t('order.files.cannotAddFiles') }}
      </d-alert>

      <template v-if="orderID">
        <small
          v-for="(file, index) of orderFiles"
          :key="'order-file-' + file.id + '-' + index"
          class="d-block">
          <a
            href="#"
            @click.prevent="downloadFile(file.id, file.file_url)">
            <div
              v-if="downloadingFile === file.id"
              class="spinner-border spinner-border-sm"
              role="status"></div>
            {{ file.name }} <template v-if="file.name">({{ file.file }})</template><template v-else>{{ file.file }}</template>
          </a>
          <a
            v-if="['.jpg', '.png'].indexOf(file.file.substr(-4)) > -1 || ['.jpeg', '.webp'].indexOf(file.file.substr(-5)) > -1 || file.file.substr(-4) === '.pdf'"
            href="#"
            @click.prevent="downloadFile(file.id, file.file_url, true, file.file.substr(-4) === '.pdf')"
            class="ml-1 mr-1">
            <i class="material-icons text-primary">search</i>
          </a>
          <i
            v-if="!readOnly"
            class="material-icons text-primary"
            @click.prevent="editFile(file.id, file.name, index)">edit</i>
          <div
            v-if="deleteInProgress === file.id"
            class="spinner-border spinner-border-sm"
            role="status"></div>
          <i
            v-if="!readOnly && deleteInProgress !== file.id"
            class="material-icons text-danger"
            :style="'opacity:' + (deleteInProgress ? '0.25' : '1') + ';'"
            @click.prevent="deleteFile(file.id, index)">delete</i>
        </small>
        <small
          v-if="!orderFiles.length"
          class="d-block text-center">
          {{ $t('order.files.noFilesToDisplay') }}
        </small>
      </template>
    </fieldset>
  </div>
</template>

<script>
import Vue from 'vue';
import jsonToFormData from 'json-form-data';
import FormUtils from './../../utils/FormUtils.js';

export default {
  name: 'order-files',
  props: [
    'isCustomerRequest',
    'orderID',
    'orderFiles',
    'readOnly'
  ],
  data () {
    return {
      deleteInProgress: false,
      downloadingFile: false,
      removingFile: false,
      uploadInputVisible: true,
      uploadInProgress: false,
      uploadForm: {
        files: null,
        name: ''
      },
      token: ''
    };
  },
  mounted () {
    if (this.isCustomerRequest) {
      this.token = window.crypto.randomUUID();
    }
  },
  methods: {
    uploadFile (fileIndex) {
      let uploadData = {
        order_id: this.orderID,
        name: this.uploadForm.name,
        file: this.uploadForm.files[fileIndex]
      };

      if (this.token) {
        uploadData.token = this.token;
      }

      this.$http.request({
        method: 'POST',
        url: '/orders/files/store',
        headers: {
          'Content-Type': undefined
        },
        transformRequest (data) {
          let dataToSend = jsonToFormData(data);
          return dataToSend;
        },
        data: uploadData
      }).then((response) => {
        if (response.data.success) {
          this.orderFiles.push(response.data.item);
        }

        if (fileIndex === this.uploadForm.files.length - 1) {
          this.uploadInProgress = false;
          this.uploadForm.files = null;
          this.uploadInputVisible = false;

          Vue.nextTick(() => {
            this.uploadInputVisible = true;
          });
        } else {
          fileIndex++;
          this.uploadFile(fileIndex);
        }
      }).catch(error => {
        console.log(error);
        Vue.swal({
          title: this.$t('order.files.uploadError'),
          html: this.$t('ui.tryAgain'),
          type: 'warning',
          confirmButtonText: this.$t('ui.ok'),
          buttonsStyling: true
        });
        this.uploadInProgress = false;
      });
    },
    downloadFile (fileID, fileURL, showPreview = false, showPDF = false) {
      if (this.downloadingFile) {
        return;
      }

      this.downloadingFile = fileID;

      this.$http.post(fileURL, null, {
        responseType: 'blob'
      }).then(response => {
        const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));

        if (showPreview) {
          if (showPDF) {
            window.open(url, '_blank');
            setTimeout(() => window.URL.revokeObjectURL(url), 5000);
          } else {
            this.$bus.$emit('image-preview-popup-show', url);
          }

          this.downloadingFile = false;
          return;
        }

        const link = document.createElement('a');
        link.href = url;
        let filename = response.request.getResponseHeader('content-disposition').split('filename=')[1].split(';')[0];
        link.setAttribute('download', filename.replace(/"/gmi, ''));
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        this.downloadingFile = false;
      }).catch(error => {
        console.log(error);
        Vue.swal({
          title: this.$t('ui.errorOccuredDuringDownload'),
          html: this.$t('ui.tryAgain'),
          type: 'warning',
          confirmButtonText: this.$t('ui.ok'),
          buttonsStyling: true
        });
        this.downloadingFile = false;
      });
    },
    deleteFile (fileID, index) {
      if (this.deleteInProgress) {
        return;
      }

      Vue.swal({
        title: this.$t('order.files.removeFileTitle'),
        text: this.$t('order.files.removeFileDesc'),
        type: 'question',
        showCancelButton: true,
        confirmButtonText: this.$t('order.files.deleteFile'),
        cancelButtonText: this.$t('ui.cancel')
      }).then((result) => {
        if (result.value) {
          this.deleteInProgress = fileID;

          this.$http.post('/orders/files/delete/' + fileID).then(response => {
            if (response.data.success) {
              Vue.swal({
                title: this.$t('purchase.fileDeletedTitle'),
                html: this.$t('purchase.fileDeletedDesc'),
                type: 'success',
                confirmButtonText: this.$t('ui.ok'),
                buttonsStyling: true
              });

              this.orderFiles.splice(index, 1);
              this.deleteInProgress = false;
            } else {
              Vue.swal({
                title: this.$t('ui.errorOccured'),
                html: this.$t('ui.tryAgain'),
                type: 'warning',
                confirmButtonText: this.$t('ui.ok'),
                buttonsStyling: true
              });
              this.deleteInProgress = false;
            }
          }).catch(error => {
            console.log(error);
            Vue.swal({
              title: this.$t('ui.errorOccured'),
              html: this.$t('ui.tryAgain'),
              type: 'warning',
              confirmButtonText: this.$t('ui.ok'),
              buttonsStyling: true
            });
            this.deleteInProgress = false;
          });
        }
      });
    },
    editFile (fileID, fileName, index) {
      Vue.swal({
        title: this.$t('order.files.editFileTitle'),
        text: this.$t('order.files.editFileText'),
        type: 'question',
        input: 'text',
        inputPlaceholder: '',
        inputValue: fileName,
        inputValidator: () => false,
        showCancelButton: true,
        confirmButtonText: this.$t('order.files.editFile'),
        cancelButtonText: this.$t('ui.cancel')
      }).then((result) => {
        if (result.value || result.value === '') {
          FormUtils.submitInBackground({
            $http: this.$http,
            endpoint: '/orders/files/update/' + fileID,
            formData: {
              name: result.value
            },
            errorTitle: this.$t('ui.errorOccured'),
            errorText: this.$t('order.files.fileEditError'),
            successAction: (response) => {
              if (response.success) {
                this.orderFiles[index].name = response.item.name;
              }
            }
          });
        }
      });
    },
    setFiles (event) {
      this.uploadForm.files = event.target.files;

      if (this.uploadForm.files) {
        this.uploadForm.name = '';
        this.uploadInProgress = true;
        this.uploadFile(0);
      }
    }
  }
}
</script>

<style lang="scss">
.order-files {
  fieldset {
    border: 1px solid #ddd;
    margin-top: 10px;
    padding: 0 20px 15px 20px;

    legend {
      font-size: 16px;
      padding: 0 10px;
    }
  }
}
</style>
